import { useEffect } from 'react';
import { useLocation, useNavigate, type Location } from 'react-router';

/*
 * Chats keep a plain address: an open chat lives in the browser's history entry (/messages + {chat: id}), not
 * in the address bar, so which chats were opened does not show in the address, in screenshots or in the
 * browser's history list. The history entry survives a reload (the chat comes back) and "back" returns to
 * the list. Links to /messages/<id> (notifications, starred messages, shared links) keep working: they are
 * turned into the plain form as soon as they open.
 */

const CHAT_PATH = /^\/messages\/(?!new$)([^/]+)$/;

/** The chat open at this location, from either form. */
export function chatIdFrom(location: Pick<Location, 'pathname' | 'state'>): string | null {
  const m = CHAT_PATH.exec(location.pathname);
  if (m) return decodeURIComponent(m[1]!);
  if (location.pathname === '/messages') {
    const chat = (location.state as { chat?: unknown } | null)?.chat;
    return typeof chat === 'string' ? chat : null;
  }
  return null;
}

export function useOpenChatId(): string | null {
  return chatIdFrom(useLocation());
}

/** Rewrites /messages/<id> to /messages with the chat in the history entry (same entry, nothing added). */
export function ChatAddressMask() {
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const m = CHAT_PATH.exec(location.pathname);
    if (!m) return;
    // A notification's link says where its message is (?seq=), so a far one loads in big steps (state.jumpSeq).
    const seq = Number(new URLSearchParams(location.search).get('seq'));
    const state = {
      ...((location.state as Record<string, unknown> | null) ?? {}),
      chat: decodeURIComponent(m[1]!),
      ...(Number.isInteger(seq) && seq > 0 ? { jumpSeq: seq } : {}),
    };
    navigate({ pathname: '/messages', hash: location.hash }, { replace: true, state });
  }, [location, navigate]);
  return null;
}
